import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionHistory } from '@/hooks/useSessionHistory';
import { useSessionMutations } from '@/hooks/mutations/useSessionMutations';
import { usePendingJobs } from '@/hooks/usePendingJobs';
import { apiClient } from '@/utils/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LoadingSpinner from '@/components/LoadingSpinner';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { AudioPlayerCoordinatorProvider } from '@/contexts/AudioPlayerCoordinator';
import { ShareWithCommunityModal } from '@/components/ShareWithCommunityModal';
import {
  Calendar,
  Share2,
  Heart,
  MoreHorizontal,
  Play,
  RefreshCw,
  Search,
  Star,
  Trash2,
  Filter,
  Clock,
  AlertCircle,
  Check,
  CheckSquare,
  Square,
  ArrowUpDown,
  Users,
  Edit,
  Gift,
  Send,
  Loader2,
  Eye,
  Lock,
  FileEdit,
  MessageCircle,
  Globe,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAudioGift } from '@/hooks/useAudioGift';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDistanceToNow } from 'date-fns';
import { SessionRecord } from '@/types/session';

const SessionHistoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [playingSession, setPlayingSession] = useState<string | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'category'>('date');
  const [highlightedSession, setHighlightedSession] = useState<string | null>(null);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingSessionName, setEditingSessionName] = useState<string>('');
  const [retryingSessionId, setRetryingSessionId] = useState<string | null>(null);

  // Refs for session cards to enable scrolling
  const sessionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const {
    sessions = [],
    favoritesSessions = [],
    sessionsByCategory = {},
    recentSessions = [],
    totalCount = 0,
    hasMore = false,
    loadMore,
    isLoadingMore = false,
    isLoading,
    error,
    refetch
  } = useSessionHistory(selectedCategory === 'all' ? undefined : selectedCategory, 5);

  const { dlqJobs = [], totalPending = 0 } = usePendingJobs();
  const {
    toggleFavorite,
    deleteSession,
    deleteSessionsBulk,
    togglingFavorite,
    deletingSession,
    isDeleting
  } = useSessionMutations();

  // Gift resend functionality
  const { sendGiftEmail, getGiftEmailPreview } = useAudioGift();
  const [resendGiftSession, setResendGiftSession] = useState<SessionRecord | null>(null);
  const [resendGiftEmail, setResendGiftEmail] = useState<string>('');
  const [resendGiftMessage, setResendGiftMessage] = useState<string>('');
  const [resendEmailPreviewHtml, setResendEmailPreviewHtml] = useState<string>('');
  const [showResendEmailPreview, setShowResendEmailPreview] = useState<boolean>(false);

  // Filter sessions based on search only (category is already filtered by the hook)
  const filteredSessions = sessions.filter(session => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return session.title.toLowerCase().includes(searchLower);
  }).sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'category':
        return a.category_id.localeCompare(b.category_id);
      case 'date':
      default:
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
  });

  // Get unique categories for filter
  const categories = Array.from(new Set(sessions.map(s => s.category_id)));

  // Handle highlight query parameter from email deep links
  useEffect(() => {
    const highlightId = searchParams.get('highlight');

    if (highlightId && !isLoading && sessions.length > 0) {
      setHighlightedSession(highlightId);

      // Wait for next tick to ensure DOM is updated
      setTimeout(() => {
        const sessionElement = sessionRefs.current[highlightId];

        if (sessionElement) {
          sessionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Remove highlight after 5 seconds
          setTimeout(() => {
            setHighlightedSession(null);
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('highlight');
            setSearchParams(newParams, { replace: true });
          }, 5000);
        } else {
          // If session not found, try to switch to 'all' category
          const session = sessions.find(s => s.id === highlightId);
          if (session && session.category_id !== selectedCategory) {
            setSelectedCategory('all');
          }
        }
      }, 100);
    }
  }, [searchParams, isLoading, sessions, selectedCategory, setSearchParams]);

  const [sharingSession, setSharingSession] = useState<string | null>(null);
  const [copiedShare, setCopiedShare] = useState(false);
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [communityShareSession, setCommunityShareSession] = useState<SessionRecord | null>(null);

  const toggleSessionSelection = (sessionId: string) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedSessions.size === filteredSessions.length && filteredSessions.length > 0) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(filteredSessions.map(s => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) return;

    // Use the bulk delete function which handles confirmation, optimistic updates, and rollback
    await deleteSessionsBulk(Array.from(selectedSessions));
    setSelectedSessions(new Set());
  };

  const handleBulkFavorite = async () => {
    if (selectedSessions.size === 0) return;

    for (const sessionId of selectedSessions) {
      await toggleFavorite(sessionId, sessions);
    }

    setSelectedSessions(new Set());
  };

  const handleRenameSession = async (sessionId: string, newTitle: string) => {
    try {
      await apiClient.patch(`/sessions/${sessionId}`, { title: newTitle });
      await refetch(); // Refresh the session list
      setEditingSessionId(null);
      setEditingSessionName('');
    } catch (error) {
      console.error('Failed to rename session:', error);
    }
  };

  const startEditingName = (session: SessionRecord) => {
    setEditingSessionId(session.id);
    setEditingSessionName(session.title);
  };

  const cancelEditingName = () => {
    setEditingSessionId(null);
    setEditingSessionName('');
  };

  const saveEditingName = async (sessionId: string) => {
    await handleRenameSession(sessionId, editingSessionName);
  };

  const handleShare = async (session: SessionRecord) => {
    if (!session.id) return;

    setSharingSession(session.id);
    try {
      // Try to get existing share link first
      let shareUrl;
      try {
        const data = await apiClient.get(`/sessions/${session.id}/share`);
        shareUrl = data.share_url;
      } catch (error) {
        // Create new share link if not found
        const data = await apiClient.post(`/sessions/${session.id}/share`, {});
        shareUrl = data.share_url;
      }

      // Try native share API first (mobile/desktop)
      if (navigator.share) {
        await navigator.share({
          title: session.title,
          text: `Check out this meditation session: ${session.title}`,
          url: shareUrl,
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareUrl);
        setCopiedShare(true);
        setTimeout(() => setCopiedShare(false), 2000);
      }
    } catch (error) {
      console.error('Share failed:', error);
    } finally {
      setSharingSession(null);
    }
  };

  const handleRetrySession = async (sessionId: string) => {
    setRetryingSessionId(sessionId);
    try {
      const response = await apiClient.post(`/sessions/${sessionId}/retry`, {});
      console.log('Retry initiated:', response);
      // Refetch sessions to update the UI
      refetch();
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setRetryingSessionId(null);
    }
  };

  const openResendGiftModal = (session: SessionRecord) => {
    setResendGiftSession(session);
    setResendGiftEmail(session.gift_recipient_email || '');
    setResendGiftMessage(session.gift_message || '');
    setResendEmailPreviewHtml('');
    setShowResendEmailPreview(false);
  };

  const handleLoadResendEmailPreview = async () => {
    if (!resendGiftSession) return;

    try {
      const result = await getGiftEmailPreview.mutateAsync({
        sessionId: resendGiftSession.id,
        recipientEmail: resendGiftEmail,
        giftMessage: resendGiftMessage || undefined,
      });
      setResendEmailPreviewHtml(result.html);
      setShowResendEmailPreview(true);
    } catch (error) {
      console.error('Failed to load email preview:', error);
    }
  };

  const handleResendGiftEmail = async () => {
    if (!resendGiftSession || !resendGiftEmail) return;

    try {
      await sendGiftEmail.mutateAsync({
        sessionId: resendGiftSession.id,
        recipientEmail: resendGiftEmail,
        giftMessage: resendGiftMessage || undefined,
      });
      setResendGiftSession(null);
      setResendGiftEmail('');
      setResendGiftMessage('');
      setResendEmailPreviewHtml('');
      setShowResendEmailPreview(false);
    } catch (error) {
      console.error('Failed to resend gift email:', error);
      // Error toast is handled by the mutation
    }
  };

  const SessionCard = React.memo(({ session }: { session: SessionRecord }) => {
    const isAudioExpired = !session.audio_url || session.audio_url.includes('blob:');
    const isSessionComplete = session.delivery_status === 'created';
    const hasWorkingAudio = session.audio_url && !isAudioExpired && isSessionComplete;
    const isHighlighted = highlightedSession === session.id;
    const hasBackgroundMusic = !!session.background_music_file;

    return (
      <Card
        ref={(el) => (sessionRefs.current[session.id] = el)}
        className={`w-full transition-all duration-500 ${
          isHighlighted
            ? 'ring-4 ring-primary ring-offset-2 shadow-lg bg-primary/5'
            : ''
        }`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {editingSessionId === session.id ? (
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    value={editingSessionName}
                    onChange={(e) => setEditingSessionName(e.target.value)}
                    placeholder="Session title..."
                    className="h-8 text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveEditingName(session.id);
                      } else if (e.key === 'Escape') {
                        cancelEditingName();
                      }
                    }}
                  />
                  <Button size="sm" onClick={() => saveEditingName(session.id)} className="h-8">
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={cancelEditingName} className="h-8">
                    Cancel
                  </Button>
                </div>
              ) : (
                <CardTitle className="text-lg font-medium mb-1">{session.title}</CardTitle>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>{formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}</span>
                <Badge variant="outline" className="ml-2">
                  {session.category_id}
                </Badge>
                {session.is_favorite && (
                  <Heart className="w-4 h-4 text-red-500 fill-current" />
                )}
                {isAudioExpired && (
                  <Badge variant="destructive" className="text-xs">
                    Audio Expired
                  </Badge>
                )}
                {/* Draft/Saved status badges */}
                {hasBackgroundMusic && !session.is_saved && (
                  <Badge variant="outline" className="text-xs border-amber-500 text-amber-600 dark:text-amber-400">
                    <FileEdit className="h-3 w-3 mr-1" />
                    Draft
                  </Badge>
                )}
                {session.is_saved && (
                  <Badge variant="outline" className="text-xs border-green-500 text-green-600 dark:text-green-400">
                    <Lock className="h-3 w-3 mr-1" />
                    Saved
                  </Badge>
                )}
                {/* Draft expiry countdown - shows for unsaved sessions */}
                {!session.is_saved && session.access_expires_at && (() => {
                  const expiresAt = new Date(session.access_expires_at);
                  const now = new Date();
                  const hoursRemaining = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60);
                  const isExpiringSoon = hoursRemaining < 2 && hoursRemaining > 0;
                  const isExpired = hoursRemaining <= 0;

                  if (isExpired) return null; // Will be cleaned up by backend

                  return (
                    <Badge
                      variant="outline"
                      className={`text-xs ${isExpiringSoon
                        ? 'border-red-500 text-red-600 dark:text-red-400'
                        : 'border-amber-500 text-amber-600 dark:text-amber-400'}`}
                    >
                      <Clock className="h-3 w-3 mr-1" />
                      Expires {formatDistanceToNow(expiresAt, { addSuffix: true })}
                    </Badge>
                  );
                })()}
                {/* Community sharing status */}
                {session.community_status && (
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      session.community_status === 'approved'
                        ? 'border-green-500 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30'
                        : session.community_status === 'pending'
                        ? 'border-yellow-500 text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/30'
                        : 'border-gray-500 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <Globe className="h-3 w-3 mr-1" />
                    {session.community_status === 'approved' ? (
                      <>
                        Community
                        {(session.community_like_count || 0) > 0 && (
                          <span className="ml-1.5 flex items-center gap-0.5">
                            <Heart className="h-3 w-3" />
                            {session.community_like_count}
                          </span>
                        )}
                        {(session.community_comment_count || 0) > 0 && (
                          <span className="ml-1 flex items-center gap-0.5">
                            <MessageCircle className="h-3 w-3" />
                            {session.community_comment_count}
                          </span>
                        )}
                      </>
                    ) : session.community_status === 'pending' ? (
                      'Pending Review'
                    ) : session.community_status === 'rejected' ? (
                      'Not Approved'
                    ) : session.community_status === 'user_revoked' ? (
                      'Withdrawn'
                    ) : (
                      'Revoked'
                    )}
                  </Badge>
                )}
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-50 bg-popover border border-border shadow-lg">
                <DropdownMenuItem
                  onClick={() => startEditingName(session)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Rename Session
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => toggleFavorite(session.id, sessions)}
                  disabled={togglingFavorite}
                >
                  <Star className="w-4 h-4 mr-2" />
                  {session.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </DropdownMenuItem>

                {hasWorkingAudio && (
                  <>
                    <DropdownMenuItem
                      onClick={() => handleShare(session)}
                      disabled={sharingSession === session.id}
                    >
                      {copiedShare && sharingSession === session.id ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Link Copied!
                        </>
                      ) : (
                        <>
                          <Share2 className="w-4 h-4 mr-2" />
                          Share Session
                        </>
                      )}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => {
                        setCommunityShareSession(session);
                        setShowCommunityModal(true);
                      }}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Share with Community
                    </DropdownMenuItem>
                  </>
                )}

                {/* Resend Gift option for gift sessions */}
                {session.is_gift && hasWorkingAudio && (
                  <DropdownMenuItem
                    onClick={() => openResendGiftModal(session)}
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    Resend Gift Email
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onClick={() => deleteSession(session.id, sessions)}
                  disabled={isDeleting(session.id)}
                  className="text-destructive"
                >
                  {isDeleting(session.id) ? (
                    <>
                      <span className="w-4 h-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full inline-block" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Session
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        
        <CardContent>
          {hasWorkingAudio ? (
            <div className="space-y-2">
              <EnhancedAudioPlayer
                audioUrl={session.audio_url}
                sessionTitle={session.title}
                sessionId={session.id}
                backgroundAudioUrl={
                  session.background_audio_url ||
                  (session.background_music_file
                    ? `${import.meta.env.VITE_BACKEND_URL || '/api/v1'}/background-music/files/${session.background_music_file}`
                    : undefined)
                }
                onEnded={() => setPlayingSession(null)}
                isSaved={session.is_saved}
                onSaved={() => refetch()}
              />
              {hasBackgroundMusic && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="secondary" className="text-xs">
                    🎵 With Background Music
                  </Badge>
                </div>
              )}
            </div>
          ) : session.delivery_status === 'failed' ? (
            <div className="p-4 bg-destructive/10 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium">Generation failed</span>
                {(session.generation_retry_count ?? 0) > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Attempt {session.generation_retry_count}/3
                  </Badge>
                )}
              </div>
              {session.last_error && (
                <p className="text-sm text-muted-foreground truncate" title={session.last_error}>
                  {session.last_error}
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleRetrySession(session.id)}
                  disabled={retryingSessionId === session.id}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${retryingSessionId === session.id ? 'animate-spin' : ''}`} />
                  {retryingSessionId === session.id ? 'Retrying...' : 'Retry'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteSession(session.id, sessions)}
                  disabled={isDeleting(session.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          ) : session.delivery_status === 'pending' ? (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm">Generating...</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Play className="w-4 h-4" />
                <span className="text-sm">Audio unavailable</span>
              </div>
            </div>
          )}
          
          {session.duration && (
            <div className="text-xs text-muted-foreground mt-2">
              Duration: {Math.round(session.duration)} minutes
            </div>
          )}
        </CardContent>
      </Card>
    );
  });

  SessionCard.displayName = 'SessionCard';

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <LoadingSpinner />
          <p className="text-sm text-muted-foreground">Loading your meditation sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              Error Loading Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              We couldn't load your meditation sessions. Please try refreshing the page.
            </p>
            <p className="text-xs text-muted-foreground font-mono mb-4">
              Error: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <Button onClick={() => refetch()} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AudioPlayerCoordinatorProvider>
      <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Session History</h1>
          <p className="text-muted-foreground">
            Your meditation sessions are saved for 7 days. Share them with others via permanent links.
          </p>
        </div>

        <Badge variant="outline" className="text-sm">
          {sessions.length} of {totalCount} Total Sessions
        </Badge>
      </div>

      {/* DLQ Jobs Section - Pending Retry */}
      {totalPending > 0 && (
        <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <CardTitle className="text-lg">Pending Audio Generation</CardTitle>
                <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/50 dark:text-orange-300">
                  {totalPending} {totalPending === 1 ? 'job' : 'jobs'}
                </Badge>
              </div>
              <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-orange-800 dark:text-orange-200">
                The audio service is temporarily unavailable. Your meditation sessions will be automatically generated when the service is restored (checked every 10 minutes).
              </p>
              {dlqJobs.slice(0, 3).map((job) => (
                <div key={job.id} className="flex items-start gap-3 p-3 bg-white dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-800">
                  <RefreshCw className="h-4 w-4 mt-0.5 text-orange-600 dark:text-orange-400 animate-spin" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      Meditation session queued for retry
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Created {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
              {dlqJobs.length > 3 && (
                <p className="text-xs text-orange-700 dark:text-orange-300 text-center">
                  And {dlqJobs.length - 3} more pending...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filter Controls */}
      <div className="space-y-4">
        <div className="flex gap-4 items-center">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Select
            value={selectedCategory}
            onValueChange={(val) => {
              setSelectedCategory(val);
              setSearchTerm(''); // Clear search when changing category
            }}
          >
            <SelectTrigger className="w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="z-50 bg-popover border border-border shadow-lg">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>
                  {category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
            <SelectTrigger className="w-48">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent className="z-50 bg-popover border border-border shadow-lg">
              <SelectItem value="date">Newest First</SelectItem>
              <SelectItem value="title">Title (A-Z)</SelectItem>
              <SelectItem value="category">Category</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions Bar */}
        {filteredSessions.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-card rounded-lg border">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSelectAll}
                className="h-8 w-8 p-0"
              >
                {selectedSessions.size === filteredSessions.length && filteredSessions.length > 0 ? (
                  <CheckSquare className="h-4 w-4 text-primary" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedSessions.size > 0 ? (
                  <Badge variant="secondary">{selectedSessions.size} selected</Badge>
                ) : (
                  `${filteredSessions.length} session${filteredSessions.length !== 1 ? 's' : ''}`
                )}
              </span>
            </div>

            {selectedSessions.size > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkFavorite}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Toggle Favorite
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Session Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">All Sessions</TabsTrigger>
          <TabsTrigger value="recent">Recent (7 days)</TabsTrigger>
          <TabsTrigger value="favorites">Favorites</TabsTrigger>
          <TabsTrigger value="categories">By Category</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <div className="grid gap-4">
            {filteredSessions.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No sessions found. Create your first meditation session to get started!</p>
              </Card>
            ) : (
              <>
                {filteredSessions.map((session) => (
                  <div key={session.id} className="relative">
                    <div className="absolute top-4 left-4 z-10">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSessionSelection(session.id);
                        }}
                        className="h-6 w-6 p-0 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 rounded border"
                      >
                        {selectedSessions.has(session.id) ? (
                          <CheckSquare className="h-4 w-4 text-primary" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <SessionCard session={session} />
                  </div>
                ))}
                {hasMore && !searchTerm && (
                  <Card className="p-6 text-center border-dashed">
                    <p className="text-sm text-muted-foreground mb-3">
                      Showing {sessions.length} of {totalCount} sessions
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => loadMore()}
                      disabled={isLoadingMore}
                      className="min-w-[200px]"
                    >
                      {isLoadingMore ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          Load More Sessions
                        </>
                      )}
                    </Button>
                  </Card>
                )}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="recent" className="mt-6">
          <div className="grid gap-4">
            {recentSessions.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No recent sessions. Create a new meditation session!</p>
              </Card>
            ) : (
              recentSessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="favorites" className="mt-6">
          <div className="grid gap-4">
            {favoritesSessions.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No favorite sessions yet. Mark sessions as favorites to save them here!</p>
              </Card>
            ) : (
              favoritesSessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="space-y-6">
            {Object.entries(sessionsByCategory).map(([categoryId, categorySessions]) => (
              <div key={categoryId}>
                <h3 className="text-xl font-semibold mb-3 capitalize">{categoryId}</h3>
                <div className="grid gap-4">
                  {categorySessions.map((session) => (
                    <SessionCard key={session.id} session={session} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Community Share Modal */}
      <ShareWithCommunityModal
        isOpen={showCommunityModal}
        onClose={() => {
          setShowCommunityModal(false);
          setCommunityShareSession(null);
        }}
        sessionId={communityShareSession?.id}
        sessionTitle={communityShareSession?.title}
      />

      {/* Resend Gift Email Dialog */}
      <Dialog open={!!resendGiftSession} onOpenChange={(open) => {
        if (!open) {
          setResendGiftSession(null);
          setResendGiftEmail('');
          setResendGiftMessage('');
          setResendEmailPreviewHtml('');
          setShowResendEmailPreview(false);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-pink-500" />
              Resend Gift Email
            </DialogTitle>
            <DialogDescription>
              Send this gift meditation to a recipient via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {resendGiftSession && (
              <div className="text-sm text-muted-foreground p-3 bg-muted rounded-lg">
                <p><strong>Session:</strong> {resendGiftSession.title}</p>
                {resendGiftSession.sender_name && (
                  <p><strong>From:</strong> {resendGiftSession.sender_name}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="resend-email">Recipient Email</Label>
              <Input
                id="resend-email"
                type="email"
                placeholder="recipient@email.com"
                value={resendGiftEmail}
                onChange={(e) => {
                  setResendGiftEmail(e.target.value);
                  setShowResendEmailPreview(false);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="resend-gift-message">Gift Message (optional)</Label>
              <Textarea
                id="resend-gift-message"
                placeholder="Add a personal message to your gift..."
                value={resendGiftMessage}
                onChange={(e) => {
                  setResendGiftMessage(e.target.value);
                  setShowResendEmailPreview(false);
                }}
                rows={3}
              />
            </div>

            <Button
              variant="outline"
              onClick={handleLoadResendEmailPreview}
              disabled={!resendGiftEmail || getGiftEmailPreview.isPending}
              className="w-full"
            >
              {getGiftEmailPreview.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading Preview...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Email
                </>
              )}
            </Button>

            {showResendEmailPreview && resendEmailPreviewHtml && (
              <div className="space-y-2">
                <Label>Email Preview</Label>
                <div className="border rounded-lg overflow-hidden bg-white">
                  <iframe
                    srcDoc={resendEmailPreviewHtml}
                    className="w-full h-[300px]"
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResendGiftSession(null);
                setResendGiftEmail('');
                setResendGiftMessage('');
                setResendEmailPreviewHtml('');
                setShowResendEmailPreview(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResendGiftEmail}
              disabled={!resendGiftEmail || sendGiftEmail.isPending}
            >
              {sendGiftEmail.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AudioPlayerCoordinatorProvider>
  );
};

export default SessionHistoryPage;